import type { ObPlanId, BillingInterval } from '../../../gql/graphql'

import { OBPlanIdValues } from '../types'

/**
 * Configuration for plan comparison utilities
 */
export interface PlanComparisonConfig {
  /** Current user's plan ID */
  currentPlanId: ObPlanId
  /** Current channel count for the user */
  currentChannelCount: number
  /** Selected channel count for comparison */
  selectedChannelCount: number
  /** Current billing cycle for the user */
  currentBillingCycle?: BillingInterval
  /** Selected billing cycle for comparison */
  selectedBillingCycle?: BillingInterval
}

/**
 * Different modes of plan comparison
 */
export enum ComparisonMode {
  /** Compare based on plan tier only */
  PLAN_TIER = 'plan_tier',
  /** Compare including channel count changes */
  WITH_CHANNELS = 'with_channels',
  /** Strict comparison (plan and channels must match exactly) */
  STRICT = 'strict',
}

/**
 * The plan hierarchy from lowest to highest tier
 */
export const PLAN_HIERARCHY: ObPlanId[] = [
  OBPlanIdValues.free,
  OBPlanIdValues.essentials,
  OBPlanIdValues.team,
  OBPlanIdValues.agency,
]

/**
 * Get the index of a plan in the hierarchy
 */
export function getHierarchyIndex(planId: ObPlanId): number {
  return PLAN_HIERARCHY.indexOf(planId)
}

/**
 * Check if a plan exists in the hierarchy
 */
export function isValidPlan(planId: ObPlanId): boolean {
  return PLAN_HIERARCHY.includes(planId)
}

/**
 * Get the relative position of target plan compared to current plan
 */
export function getRelativePosition(
  targetPlanId: ObPlanId,
  currentPlanId: ObPlanId,
): 'upgrade' | 'downgrade' | 'same' | 'invalid' {
  const currentIndex = getHierarchyIndex(currentPlanId)
  const targetIndex = getHierarchyIndex(targetPlanId)

  if (currentIndex === -1 || targetIndex === -1) {
    return 'invalid'
  }

  if (targetIndex > currentIndex) return 'upgrade'
  if (targetIndex < currentIndex) return 'downgrade'
  return 'same'
}

/**
 * Check if channel count represents an increase
 */
export function isChannelIncrease(
  targetChannelCount: number,
  currentChannelCount: number,
): boolean {
  return targetChannelCount > currentChannelCount
}

/**
 * Check if channel count represents a decrease
 */
export function isChannelDecrease(
  targetChannelCount: number,
  currentChannelCount: number,
): boolean {
  return targetChannelCount < currentChannelCount
}

/**
 * Check if the given plan is the current plan
 */
export function isPlanCurrent(
  targetPlanId: ObPlanId,
  config: PlanComparisonConfig,
  mode: ComparisonMode = ComparisonMode.PLAN_TIER,
  targetChannelCount?: number,
): boolean {
  const { currentPlanId, currentChannelCount, selectedChannelCount } = config
  const planMatches = targetPlanId === currentPlanId

  switch (mode) {
    case ComparisonMode.PLAN_TIER:
      return planMatches
    case ComparisonMode.WITH_CHANNELS:
      return (
        planMatches &&
        (targetChannelCount ?? selectedChannelCount) === currentChannelCount
      )
    case ComparisonMode.STRICT:
      return (
        planMatches &&
        (targetChannelCount ?? selectedChannelCount) === currentChannelCount
      )
    default:
      return planMatches
  }
}

/**
 * Check if the target plan is an upgrade from the current plan
 */
export function isPlanUpgrade(
  targetPlanId: ObPlanId,
  config: PlanComparisonConfig,
  mode: ComparisonMode = ComparisonMode.PLAN_TIER,
): boolean {
  const { currentPlanId, selectedChannelCount } = config
  const relativePosition = getRelativePosition(targetPlanId, currentPlanId)

  switch (mode) {
    case ComparisonMode.PLAN_TIER:
      return relativePosition === 'upgrade'
    case ComparisonMode.WITH_CHANNELS:
      // Consider it an upgrade if plan tier increases OR same plan with more channels
      return (
        relativePosition === 'upgrade' ||
        (relativePosition === 'same' &&
          isChannelIncrease(selectedChannelCount, config.currentChannelCount))
      )
    case ComparisonMode.STRICT:
      return relativePosition === 'upgrade'
    default:
      return relativePosition === 'upgrade'
  }
}

/**
 * Check if the target plan is a downgrade from the current plan
 */
export function isPlanDowngrade(
  targetPlanId: ObPlanId,
  config: PlanComparisonConfig,
  mode: ComparisonMode = ComparisonMode.PLAN_TIER,
): boolean {
  const { currentPlanId, selectedChannelCount } = config
  const relativePosition = getRelativePosition(targetPlanId, currentPlanId)

  switch (mode) {
    case ComparisonMode.PLAN_TIER:
      return relativePosition === 'downgrade'
    case ComparisonMode.WITH_CHANNELS:
      // Consider it a downgrade if plan tier decreases OR same plan with fewer channels
      return (
        relativePosition === 'downgrade' ||
        (relativePosition === 'same' &&
          isChannelDecrease(selectedChannelCount, config.currentChannelCount))
      )
    case ComparisonMode.STRICT:
      return relativePosition === 'downgrade'
    default:
      return relativePosition === 'downgrade'
  }
}

/**
 * Check if this represents a plan update (same plan, different channel count or billing cycle)
 * This is typically used to identify when a user is changing their channel count
 * or billing cycle on the same plan tier, which may require different UI treatment
 */
export function isPlanUpdate(
  targetPlanId: ObPlanId,
  config: PlanComparisonConfig,
  targetChannelCount?: number,
): boolean {
  const {
    currentPlanId,
    currentChannelCount,
    selectedChannelCount,
    currentBillingCycle,
    selectedBillingCycle,
  } = config
  const channelsToCompare = targetChannelCount ?? selectedChannelCount

  // Free plan doesn't support updates
  if (targetPlanId === OBPlanIdValues.free) {
    return false
  }

  // Check if it's the same plan
  if (targetPlanId !== currentPlanId) {
    return false
  }

  // Check for channel count changes
  const channelCountChanged = channelsToCompare !== currentChannelCount

  // Check for billing cycle changes (only if both values are provided)
  const billingCycleChanged =
    currentBillingCycle &&
    selectedBillingCycle &&
    currentBillingCycle !== selectedBillingCycle

  return channelCountChanged || billingCycleChanged || false
}
